import React, { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api } from "@/lib/apiClient";

const CATEGORIES = ["Agriculture", "Construction", "Engineering", "Technology", "Administration"];

export default function SignupPage() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    current_location: "Berlin, Germany",
    profession: "",
    years_experience: 0,
    interested_categories: [],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggleCat = (c) => {
    setForm((f) => ({
      ...f,
      interested_categories: f.interested_categories.includes(c)
        ? f.interested_categories.filter((x) => x !== c)
        : [...f.interested_categories, c],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/signup", { ...form, years_experience: Number(form.years_experience) });
      toast.success("You're in", { description: "Check your inbox for a welcome email." });
      setDone(true);
    } catch (err) {
      toast.error("Signup failed", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="signup-page" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Join network</div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-[#1A2E24]">
          One profile.<br /> A team in Berlin<br /> on your side.
        </h1>
        <p className="mt-6 text-[#5C6B64] leading-relaxed">
          We'll match you with US contracts before they hit the public board. Your details stay
          in our Berlin office — never sold, never spammed.
        </p>
        <ul className="mt-8 space-y-3 text-sm text-[#1A2E24]">
          {["Get matched to visa-sponsored roles", "Skip the recruiter middleman", "Free access, always"].map((t) => (
            <li key={t} className="flex items-center gap-2"><Check size={16} className="text-[#D35400]"/>{t}</li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-7">
        <div className="border border-[#DCD7C9] bg-white p-6 sm:p-10">
          {done ? (
            <div className="text-center py-10" data-testid="signup-success">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#2E4E3F]/10 text-[#2E4E3F] mb-4"><Check size={26}/></div>
              <div className="font-display text-2xl text-[#1A2E24]">Welcome aboard</div>
              <p className="mt-2 text-sm text-[#5C6B64]">A welcome email is on its way.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="signup-form">
              {[
                { name: "full_name", label: "Full name", type: "text" },
                { name: "email", label: "Email", type: "email" },
                { name: "current_location", label: "Current location", type: "text" },
                { name: "profession", label: "Profession", type: "text" },
                { name: "years_experience", label: "Years of experience", type: "number", half: true },
              ].map((f) => (
                <div key={f.name} className={f.name === "years_experience" ? "" : "sm:col-span-2"}>
                  <label className="text-xs uppercase tracking-widest text-[#5C6B64]">{f.label}</label>
                  <input
                    data-testid={`signup-${f.name}`}
                    required
                    type={f.type}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#5C6B64]">Interested categories</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => {
                    const active = form.interested_categories.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        data-testid={`signup-cat-${c}`}
                        onClick={() => toggleCat(c)}
                        className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                          active
                            ? "bg-[#1A2E24] text-[#F9F6F0] border-[#1A2E24]"
                            : "bg-white text-[#1A2E24] border-[#DCD7C9] hover:border-[#1A2E24]"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="signup-submit"
                  disabled={busy}
                  className="w-full bg-[#D35400] text-white py-3.5 text-sm font-medium hover:bg-[#E67E22] transition-colors disabled:opacity-60"
                >
                  {busy ? "Creating profile…" : "Create profile"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
